import { Book, MoreVertical } from "lucide-react";
import React from "react";
import { Button } from "../../../components/ui/button";
import { Card, CardContent } from "../../../components/ui/card";
import { useLanguage } from "../../../context/LanguageContext";
import DrawerEditLevels from "./DrawerEditLevels";
import DialogDeleteLevels from "./DialogDeleteLevels";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuTrigger,
} from "../../../components/ui/dropdown-menu";
import { LevelData } from "../../../lib/languages";
import { useAuth } from "../../../context/AuthContext";

const LevelsCard = ({ level, course, setLevelId, levelId, getAllLevels }) => {
  const { user } = useAuth();
  const { language } = useLanguage();
  const t = LevelData[language] || LevelData["en"];

  return (
    <Card className="overflow-hidden transition-all duration-300 hover:shadow-lg rounded-xl group">
      <CardContent className="p-0 grid grid-cols-1 h-full">
        {/* Left section with gradient background */}
        <div className="relative p-4 bg-gradient-to-tr from-blue-700 to-blue-400 text-white h-full">
          {/* Dropdown Menu */}
          {user?.role === "admin" && (
            <div className="absolute top-2 right-2 z-10">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 !text-white hover:bg-blue-700/30 rounded-full"
                  >
                    <MoreVertical className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-40 space-y-1">
                  <DropdownMenuGroup>
                    <DrawerEditLevels
                      level={level}
                      course={course}
                      getAllLevels={getAllLevels}
                    />
                    <DialogDeleteLevels
                      level={level}
                      course={course}
                      getAllLevels={getAllLevels}
                    />
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          <div className="flex items-center justify-center h-full gap-4 py-4">
            {/* SVG Icon (omitted for brevity) */}
            <svg
              width="70"
              height="70"
              viewBox="0 0 70 70"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="flex-shrink-0 transition-transform duration-300 group-hover:scale-105"
            >
              <g clipPath="url(#clip0_378_1990)">
                <path
                  d="M17.5 22.167C23.299 22.167 28 17.466 28 11.667C28 5.868 23.299 1.16699 17.5 1.16699C11.701 1.16699 7 5.868 7 11.667C7 17.466 11.701 22.167 17.5 22.167Z"
                  fill="#FFE0B2"
                />
                <path
                  d="M17.5 1.16668C16.9133 1.17044 16.3279 1.22312 15.75 1.32418C20.8013 2.17201 24.5013 6.54468 24.5013 11.6667C24.5013 16.7887 20.8013 21.1614 15.75 22.0092C19.6666 22.6949 23.6346 21.1095 26.0002 17.9136C28.3659 14.7177 28.7234 10.4597 26.9238 6.91415C25.1241 3.36855 21.4761 1.14365 17.5 1.16668Z"
                  fill="#DFC49C"
                />
                <path
                  d="M26.8327 45.5V64.1667C26.8327 66.744 24.7433 68.8333 22.166 68.8333C19.5887 68.8333 17.4993 66.744 17.4993 64.1667C17.4993 66.744 15.41 68.8333 12.8327 68.8333C10.2554 68.8333 8.16602 66.744 8.16602 64.1667V45.5H26.8327Z"
                  fill="#3F51B5"
                />
                <path
                  d="M23.3327 45.5V64.1667C23.3288 66.0648 22.1757 67.7715 20.416 68.4833C21.8528 69.0727 23.489 68.9061 24.7776 68.0392C26.0661 67.1724 26.8371 65.7197 26.8327 64.1667L26.8327 45.5H23.3327Z"
                  fill="#283593"
                />
                <path
                  d="M37.3327 29.832V39.7253L26.8327 29.2253V45.5003H8.16602V44.3337C8.16602 46.2667 6.59901 47.8337 4.66602 47.8337C2.73302 47.8337 1.16602 46.2667 1.16602 44.3337V32.667C1.16602 26.868 5.86703 22.167 11.666 22.167H26.2844C28.4503 22.1668 30.5277 23.0271 32.0594 24.5587L37.3327 29.832Z"
                  fill="#FFD70E"
                />
                <path
                  d="M23.334 31.5584V45.5001H26.834V29.2251H25.6673C24.3787 29.2251 23.334 30.2698 23.334 31.5584Z"
                  fill="#D0B011"
                />
                <path
                  d="M32.0602 24.5587C30.5285 23.0271 28.4512 22.1668 26.2852 22.167H22.7852C24.9512 22.1668 27.0285 23.0271 28.5602 24.5587L33.8335 29.832V36.2253L37.3335 39.7253V29.832L32.0602 24.5587Z"
                  fill="#D0B011"
                />
                <path
                  d="M40.834 1.16699H39.6673C38.3787 1.16699 37.334 2.21166 37.334 3.50033V66.5003C37.334 67.789 38.3787 68.8337 39.6673 68.8337H40.834C42.1226 68.8337 43.1673 67.789 43.1673 66.5003V3.50033C43.1673 2.21166 42.1226 1.16699 40.834 1.16699Z"
                  fill="#FFDC00"
                />
                <path
                  d="M43.166 3.5H67.666C68.3103 3.5 68.8327 4.02233 68.8327 4.66667V26.8333C68.8327 27.4777 68.3103 28 67.666 28H43.166V3.5Z"
                  fill="#FFD70E"
                />
                <path
                  d="M67.666 3.5H64.166C64.8103 3.5 65.3327 4.02233 65.3327 4.66667V26.8333C65.3327 27.4777 64.8103 28 64.166 28H67.666C68.3103 28 68.8327 27.4777 68.8327 26.8333V4.66667C68.8327 4.02233 68.3103 3.5 67.666 3.5Z"
                  fill="#D0B011"
                />
                <path
                  d="M67.6667 2.3335H44.1187C43.626 0.940086 42.3112 0.00628853 40.8333 0.000161909H39.6667C37.7337 0.000161909 36.1667 1.56717 36.1667 3.50016V27.0143L32.886 23.7325C31.1304 21.9893 28.759 21.0077 26.285 21.0002H24.4697C28.5087 17.9909 30.1604 12.7293 28.566 7.95149C26.9715 3.17372 22.4909 -0.0413757 17.4541 -0.0218845C12.4174 -0.00239338 7.96177 3.24728 6.40435 8.03725C4.84692 12.8272 6.53933 18.0759 10.6015 21.0538C4.60059 21.6095 0.00804112 26.6403 0 32.6668V44.3335C0 46.9108 2.08934 49.0002 4.66667 49.0002C5.4883 48.9953 6.29376 48.7714 7 48.3515V64.1668C7 66.6777 8.60668 68.9068 10.9887 69.7008C13.3707 70.4948 15.9935 69.6755 17.5 67.6668C17.668 67.8904 17.8508 68.1025 18.0472 68.3015C19.7173 69.9673 22.2262 70.4634 24.4046 69.5584C26.5829 68.6535 28.0019 66.5257 28 64.1668V32.0415L36.1667 40.2082V66.5002C36.1667 68.4332 37.7337 70.0002 39.6667 70.0002H40.8333C42.7663 70.0002 44.3333 68.4332 44.3333 66.5002V29.1668H67.6667C68.9553 29.1668 70 28.1222 70 26.8335V4.66683C70 3.37816 68.9553 2.3335 67.6667 2.3335ZM8.16667 11.6668C8.16667 6.51217 12.3453 2.3335 17.5 2.3335C22.6547 2.3335 26.8333 6.51217 26.8333 11.6668C26.8333 16.8215 22.6547 21.0002 17.5 21.0002C12.3477 20.9944 8.17245 16.8191 8.16667 11.6668ZM25.6667 64.1668C25.6667 66.0998 24.0997 67.6668 22.1667 67.6668C20.2337 67.6668 18.6667 66.0998 18.6667 64.1668V52.5002C18.6667 51.8558 18.1443 51.3335 17.5 51.3335C16.8557 51.3335 16.3333 51.8558 16.3333 52.5002V64.1668C16.3333 65.4173 15.6662 66.5727 14.5833 67.1979C13.5004 67.8231 12.1662 67.8231 11.0833 67.1979C10.0004 66.5727 9.33333 65.4173 9.33333 64.1668V46.6668H25.6667V64.1668ZM27.6582 28.4003C27.3245 28.0668 26.8228 27.967 26.3869 28.1475C25.951 28.3281 25.6668 28.7534 25.6667 29.2252V44.3335H9.33333V33.8335C9.33333 33.1892 8.811 32.6668 8.16667 32.6668C7.52233 32.6668 7 33.1892 7 33.8335V44.3335C7 45.6222 5.95533 46.6668 4.66667 46.6668C3.378 46.6668 2.33333 45.6222 2.33333 44.3335V32.6668C2.33912 27.5146 6.51441 23.3393 11.6667 23.3335H26.285C28.1404 23.3392 29.9189 24.0757 31.2352 25.3833L36.1667 30.3148V36.9088L27.6582 28.4003ZM42 66.5002C42 67.1445 41.4777 67.6668 40.8333 67.6668H39.6667C39.0223 67.6668 38.5 67.1445 38.5 66.5002V3.50016C38.5 2.85583 39.0223 2.3335 39.6667 2.3335H40.8333C41.4777 2.3335 42 2.85583 42 3.50016V66.5002ZM67.6667 26.8335H44.3333V4.66683H67.6667V26.8335Z"
                  fill="black"
                />
                <path
                  d="M52.5007 22.1669C51.8563 22.1669 51.334 22.6893 51.334 23.3336C51.334 23.9779 51.8563 24.5003 52.5007 24.5003H59.5007C60.145 24.5003 60.6673 23.9779 60.6673 23.3336C60.6673 22.6893 60.145 22.1669 59.5007 22.1669H57.1673V8.16694C57.1672 7.69514 56.883 7.26985 56.4471 7.08932C56.0112 6.90878 55.5095 7.00855 55.1758 7.34211L51.6758 10.8421C51.2337 11.2999 51.24 12.0275 51.69 12.4776C52.14 12.9276 52.8677 12.9339 53.3255 12.4918L54.834 10.9833V22.1669H52.5007Z"
                  fill="black"
                />
              </g>
              <defs>
                <clipPath id="clip0_378_1990">
                  <rect width="70" height="70" fill="white" />
                </clipPath>
              </defs>
            </svg>

            {/* Title */}
            <p className="font-bold text-xl md:text-2xl text-center md:text-left text-white drop-shadow-md">
              {level.title?.[language] ||
                level.title?.en ||
                level.title?.ar ||
                "---"}
            </p>
          </div>
        </div>

        {/* Right section with content */}
        <div className="break-all p-4 flex flex-col justify-between gap-4">
          <div
            className="break-all"
            dangerouslySetInnerHTML={{
              __html:
                level.description?.[language] ||
                level.description?.en ||
                level.description?.ar ||
                "---",
            }}
          />
          <Button
            className="w-full h-11 bg-gradient-to-tr from-blue-700 to-blue-400 text-white hover:from-blue-400 hover:to-blue-700 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 mt-2"
            disabled={levelId === level.id}
            onClick={() => setLevelId(level.id)}
          >
            <Book className="h-4 w-4" />
            <span>{t.viewLessons}</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default LevelsCard;
